import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_URL = import.meta.env.VITE_API_URL;

const ReclamationDetailModal = ({
  reclamation,
  onClose,
  onRefresh,
  onNavigate,
  onEdit,
  hasPermission
}) => {
  const getToken = () => localStorage.getItem('token');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [activeTab, setActiveTab] = useState('detail'); // 'detail' | 'comments' | 'history'

  if (!reclamation) return null;

  const clientName = reclamation.client
    ? `${reclamation.client.prenom || ''} ${reclamation.client.nom || ''}`.trim()
    : 'Client inconnu';

  const dossierRef = reclamation.dossier
    ? (typeof reclamation.dossier === 'object' ? (reclamation.dossier.referenceSteg || 'Dossier Pompage') : 'Dossier lié')
    : null;

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityBadge = (priorite) => {
    const map = {
      low: { label: '🟢 Faible', bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
      medium: { label: '🟡 Normale', bg: '#fefce8', color: '#854d0e', border: '#fef08a' },
      high: { label: '🟠 Élevée', bg: '#fff7ed', color: '#c2410c', border: '#ffedd5' },
      critical: { label: '🔥 CRITIQUE', bg: '#fef2f2', color: '#991b1b', border: '#fecaca' }
    };
    const p = map[priorite] || map.medium;
    return (
      <span style={{ padding: '3px 10px', borderRadius: '12px', background: p.bg, color: p.color, border: `1px solid ${p.border}`, fontWeight: 700, fontSize: '0.8rem' }}>
        {p.label}
      </span>
    );
  };

  const getStatusBadge = (statut) => {
    const map = {
      open: { label: 'Ouverte', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
      en_attente: { label: 'Ouverte', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
      in_progress: { label: 'En cours', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
      en_cours: { label: 'En cours', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
      waiting: { label: 'En attente', bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
      resolved: { label: 'Résolue', bg: '#dcfce7', color: '#166534', border: '#86efac' },
      résolue: { label: 'Résolue', bg: '#dcfce7', color: '#166534', border: '#86efac' },
      closed: { label: 'Clôturée', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
      fermée: { label: 'Clôturée', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' }
    };
    const s = map[statut] || map.open;
    return (
      <span style={{ padding: '3px 10px', borderRadius: '12px', background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 700, fontSize: '0.8rem' }}>
        {s.label}
      </span>
    );
  };

  // Changement rapide de statut
  const handleUpdateStatus = async (newStatut) => {
    const { value: comment } = await Swal.fire({
      title: `Changer le statut en "${newStatut.toUpperCase()}"`,
      input: 'textarea',
      inputLabel: 'Ajouter un commentaire (optionnel)',
      inputPlaceholder: 'Entrez une note explicative...',
      showCancelButton: true,
      confirmButtonText: 'Valider le changement',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#1e3a5f'
    });

    if (comment !== undefined) {
      try {
        await axios.patch(
          `${API_URL}/reclamations/${reclamation._id}/status`,
          { statut: newStatut, reponseAdmin: comment },
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        Swal.fire('Mis à jour !', 'Le statut a été mis à jour avec succès.', 'success');
        onRefresh();
      } catch (err) {
        console.error('Erreur mise à jour statut:', err);
        Swal.fire('Erreur', 'Impossible de mettre à jour le statut', 'error');
      }
    }
  };

  // Ajout de commentaire
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      await axios.post(
        `${API_URL}/reclamations/${reclamation._id}/comments`,
        { text: newComment.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setNewComment('');
      onRefresh();
    } catch (err) {
      console.error('Erreur ajout commentaire:', err);
      Swal.fire('Erreur', 'Impossible d\'ajouter le commentaire', 'error');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Suppression avec la CARTE SweetAlert2 IDENTIQUE aux Clients / Dossiers / Paiements
  const handleDelete = async () => {
    const result = await Swal.fire({
      title: '<span style="color:#991b1b; font-weight:800;">Action Irréversible !</span>',
      html: `
        <div style="text-align:left; font-size:14px; color:#475569; line-height:1.5;">
          <p style="margin-bottom:10px;">Vous êtes sur le point de <strong>supprimer cette réclamation</strong> :</p>
          <div style="padding:12px 14px; background:#fef2f2; border:1px solid #fecaca; border-radius:8px; font-weight:700; color:#991b1b; margin-bottom:12px;">
            ⚠️ Réclamation : ${reclamation.reference || reclamation._id}<br/>
            📌 Sujet : ${reclamation.sujet || 'Réclamation'}<br/>
            👤 Client : ${clientName}
          </div>
          <p style="color:#7f1d1d; font-size:13px; margin:0;">
            ⚠️ <strong>Impact :</strong> Cette réclamation sera définitivement retirée des listes et son historique sera archivé.
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Oui, supprimer la réclamation',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/reclamations/${reclamation._id}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        Swal.fire('Supprimé !', 'La réclamation a été supprimée avec succès.', 'success');
        onRefresh();
        onClose();
      } catch (err) {
        console.error('Erreur suppression réclamation:', err);
        Swal.fire('Erreur', 'Impossible de supprimer la réclamation', 'error');
      }
    }
  };

  return (
    <div className="rec-modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
      <div className="rec-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '780px', width: '92%', borderRadius: '16px', padding: '24px' }}>
        {/* HEADER MODAL */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', paddingBottom: '14px', borderBottom: '1.5px solid #e2e8f0' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#2563eb', background: '#eff6ff', padding: '3px 10px', borderRadius: '8px' }}>
                {reclamation.reference || 'REC-2026'}
              </span>
              {getStatusBadge(reclamation.statut)}
              {getPriorityBadge(reclamation.priorite)}
            </div>
            <h2 style={{ margin: '6px 0 0', fontSize: '1.25rem', fontWeight: 800, color: '#1e293b' }}>
              {reclamation.sujet}
            </h2>
          </div>
          <button type="button" onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontWeight: 700, color: '#64748b' }}>✕</button>
        </div>

        {/* NAVIGATION CLIENT & DOSSIER D'UN CLIC */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {reclamation.client && onNavigate && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onNavigate('client-detail', { clientId: reclamation.client._id || reclamation.client });
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: '#1e3a5f', color: '#ffffff', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
            >
              👤 Voir Fiche Client ({clientName})
            </button>
          )}

          {reclamation.dossier && onNavigate && (
            <button
              type="button"
              onClick={() => {
                onClose();
                const cId = reclamation.client?._id || reclamation.client;
                const dId = typeof reclamation.dossier === 'object' ? reclamation.dossier._id : reclamation.dossier;
                onNavigate('detail-dossier', { clientId: cId, dossierId: dId });
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: '#0284c7', color: '#ffffff', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
            >
              📂 Voir Dossier ({dossierRef || 'Dossier lié'})
            </button>
          )}

          {hasPermission && hasPermission('reclamations', 'update') && (
            <button
              type="button"
              onClick={() => {
                onClose();
                if (onEdit) onEdit(reclamation);
              }}
              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: '#f59e0b', color: '#ffffff', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
            >
              ✏️ Modifier Tous Les Champs
            </button>
          )}
        </div>

        {/* ONGLETS INTERNES */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e2e8f0', marginBottom: '16px' }}>
          <button
            type="button"
            onClick={() => setActiveTab('detail')}
            style={{ padding: '8px 16px', border: 'none', borderBottom: activeTab === 'detail' ? '2px solid #1e3a5f' : '2px solid transparent', background: 'none', fontWeight: activeTab === 'detail' ? 800 : 500, color: activeTab === 'detail' ? '#1e3a5f' : '#64748b', cursor: 'pointer' }}
          >
            📋 Détails & Pièces Jointes
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('comments')}
            style={{ padding: '8px 16px', border: 'none', borderBottom: activeTab === 'comments' ? '2px solid #1e3a5f' : '2px solid transparent', background: 'none', fontWeight: activeTab === 'comments' ? 800 : 500, color: activeTab === 'comments' ? '#1e3a5f' : '#64748b', cursor: 'pointer' }}
          >
            💬 Commentaires ({reclamation.comments?.length || 0})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            style={{ padding: '8px 16px', border: 'none', borderBottom: activeTab === 'history' ? '2px solid #1e3a5f' : '2px solid transparent', background: 'none', fontWeight: activeTab === 'history' ? 800 : 500, color: activeTab === 'history' ? '#1e3a5f' : '#64748b', cursor: 'pointer' }}
          >
            🛡️ Audit Trail & Traçabilité ({reclamation.history?.length || 0})
          </button>
        </div>

        {/* CONTENU ONGLETS */}
        <div style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
          {activeTab === 'detail' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.88rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 800 }}>Description du problème</h4>
                <p style={{ margin: 0, color: '#1e293b', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {reclamation.message}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#ffffff', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
                <div><strong>Catégorie :</strong> {reclamation.categorie || 'Autre'}</div>
                <div><strong>Assigné à :</strong> {reclamation.assignedTo ? `${reclamation.assignedTo.prenom || ''} ${reclamation.assignedTo.nom || ''}` : 'Non assigné'}</div>
                <div><strong>Créée par :</strong> {reclamation.createdBy ? `${reclamation.createdBy.prenom || ''} ${reclamation.createdBy.nom || ''}` : 'Système'}</div>
                <div><strong>Date création :</strong> {formatDate(reclamation.createdAt)}</div>
                {reclamation.resolvedAt && <div><strong>Résolue le :</strong> {formatDate(reclamation.resolvedAt)}</div>}
                {reclamation.closedAt && <div><strong>Clôturée le :</strong> {formatDate(reclamation.closedAt)}</div>}
              </div>

              {/* PIÈCES JOINTES */}
              {reclamation.attachments && reclamation.attachments.length > 0 && (
                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '0.88rem', fontWeight: 800, color: '#334155' }}>📎 Pièces Jointes ({reclamation.attachments.length})</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                    {reclamation.attachments.map((att, i) => (
                      <a
                        key={i}
                        href={`${API_URL.replace('/api', '')}${att.url}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', textDecoration: 'none', color: '#1e3a5f', fontWeight: 600, fontSize: '0.82rem' }}
                      >
                        📄 {att.originalName}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <div>
              <div style={{ marginBottom: '16px' }}>
                {(reclamation.comments || []).length > 0 ? (
                  reclamation.comments.map((c, i) => (
                    <div key={i} style={{ padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.8rem' }}>
                        <strong style={{ color: '#2563eb' }}>{c.author?.prenom ? `${c.author.prenom} ${c.author.nom}` : 'Intervenant'}</strong>
                        <span style={{ color: '#94a3b8' }}>{formatDate(c.date)}</span>
                      </div>
                      <p style={{ margin: 0, color: '#334155', fontSize: '0.9rem', lineHeight: '1.4' }}>{c.text}</p>
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>Aucun commentaire enregistré pour l'instant.</p>
                )}
              </div>

              {hasPermission && hasPermission('reclamations', 'update') && (
                <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Ajouter une note ou un suivi interne..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '0.88rem' }}
                  />
                  <button
                    type="submit"
                    disabled={submittingComment || !newComment.trim()}
                    style={{ padding: '10px 18px', background: '#1e3a5f', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Envoyer
                  </button>
                </form>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '0.88rem', fontWeight: 800, color: '#334155' }}>
                  🛡️ Historique Complet & Traçabilité (Audit Trail System)
                </h4>
                {(reclamation.history || []).length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {reclamation.history.map((h, i) => (
                      <div key={i} style={{ padding: '10px 14px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.82rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#1e293b' }}>
                          <span>{h.action} {h.field ? `(${h.field})` : ''}</span>
                          <span style={{ color: '#94a3b8', fontWeight: 500 }}>{formatDate(h.date)}</span>
                        </div>
                        <div style={{ color: '#64748b', marginTop: '2px' }}>
                          Par : <strong>{h.user?.prenom ? `${h.user.prenom} ${h.user.nom}` : 'Admin/Modérateur'}</strong>
                        </div>
                        {(h.oldVal || h.newVal) && (
                          <div style={{ marginTop: '4px', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', fontSize: '0.78rem' }}>
                            <span style={{ color: '#dc2626' }}>{h.oldVal || 'Vide'}</span> ➔ <span style={{ color: '#16a34a' }}>{h.newVal || 'Vide'}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>Aucun enregistrement d'audit pour le moment.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER ACTIONS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', paddingTop: '16px', borderTop: '1.5px solid #e2e8f0', flexWrap: 'wrap', gap: '10px' }}>
          {hasPermission && hasPermission('reclamations', 'update') && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => handleUpdateStatus('in_progress')} style={{ padding: '6px 12px', background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: '6px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                🔄 Passer En cours
              </button>
              <button type="button" onClick={() => handleUpdateStatus('waiting')} style={{ padding: '6px 12px', background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', borderRadius: '6px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                ⏳ Passer En attente
              </button>
              <button type="button" onClick={() => handleUpdateStatus('resolved')} style={{ padding: '6px 12px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                ✓ Marquer Résolue
              </button>
              <button type="button" onClick={() => handleUpdateStatus('closed')} style={{ padding: '6px 12px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                🔒 Clôturer
              </button>
            </div>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            {hasPermission && hasPermission('reclamations', 'delete') && (
              <button
                type="button"
                onClick={handleDelete}
                style={{ padding: '8px 14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                🗑️ Supprimer
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '8px 16px', background: '#cbd5e1', color: '#1e293b', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReclamationDetailModal;
